import './styles.css'
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RightOutlined, LeftOutlined } from  "@ant-design/icons"
import { useState, useEffect, useRef, useMemo } from 'react'
import { Alert } from 'antd'
import ItemCardSkeleton from '../ItemCardSkeleton'

import ItemCard from "../ItemCard";

import {
    selectCategoryState,
    fetchNextCategoryPage,
    fetchProductsByCategory,
} from "../../features/products";

import {
    categoriesSelectors
} from "../../features/categories";

import { geoSelectors } from "../../features/geolocation";

const ProductsShowcaseSkeleton = ({ itemsPerPage, isSellers, title, titleStyle, description, style }) => {
    return (
        <div className='showcase' style={style}>
            <div className="showcase-header">
                <div className={`showcase-title ${titleStyle}`}>{title || <div className="skeleton-shimmer" style={{ width: 200, height: 20, borderRadius: 6 }} />}</div>
                {!isSellers && (
                    <>
                        <div className='showcase-description'>
                            {description || <div className="skeleton-shimmer" style={{ width: 150, height: 16, borderRadius: 6 }} />}
                        </div>
                        <div className="showcase-header-ctn-btn">
                            <div className="skeleton-shimmer" style={{ width: 183, height: 30, borderRadius: 5 }} />
                            <div className="showcase-ctn-btn-arrow" style={{ marginTop: '9px' }}>
                                <div className="skeleton-shimmer" style={{ width: 27, height: 27, borderRadius: '50%' }} />
                                <div className="skeleton-shimmer" style={{ width: 27, height: 27, borderRadius: '50%' }} />
                            </div>
                        </div>
                    </>
                )}
                {isSellers && (
                    <div className="showcase-header-ctn-btn">
                        <div className="showcase-ctn-btn-arrow">
                            <div className="skeleton-shimmer" style={{ width: 27, height: 27, borderRadius: '50%' }} />
                            <div className="skeleton-shimmer" style={{ width: 27, height: 27, borderRadius: '50%' }} />
                        </div>
                    </div>
                )}
            </div>

            <div className={`showcase-container ${isSellers ? 'showcase-sellers' : ''}`}>
                {Array(itemsPerPage).fill(null).map((_, i) => (
                    isSellers ? (
                        <div key={i} className="seller-card-skeleton">
                            <div className="skeleton-shimmer" style={{ width: '100%', height: '100%', borderRadius: 10 }} />
                        </div>
                    ) : (
                        <ItemCardSkeleton key={i} />
                    )
                ))}
            </div>

        </div>
    );
};

const ProductsShowcase = (props) => {
    const {
        categoryId,
        title,
        titleStyle,
        description,
        style,
        isSellers = false,
        skipFetch = false,
        infinite = false,
    } = props

    const {
        selectIsItemLoading
    } = categoriesSelectors

    const categoryIsLoading = useSelector(state => selectIsItemLoading(state, categoryId))

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const regionCode = useSelector(geoSelectors.selectRegionCode);

    const categoryState = useSelector(state =>
        selectCategoryState(state, categoryId),
        shallowEqual
    )

    const {
        items = [],
        isLoading = false,
        error = null,
        hasMore = false,
        canLoadMore = false,
        paginationInfo = {},
    } = categoryState || {}

    useEffect(() => {
        if (!categoryId || skipFetch) return;

        const isRegionChange = prevRegionRef.current !== regionCode;
        prevRegionRef.current = regionCode;

        if (isRegionChange) {
            setCurrentIndex(0);
        }

        dispatch(fetchProductsByCategory(categoryId, { limit: 10, refresh: isRegionChange }));
    }, [dispatch, categoryId, regionCode, skipFetch]);

    const prevRegionRef = useRef(regionCode);

    const [currentIndex, setCurrentIndex] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(5)
    const [slideDirection, setSlideDirection] = useState('next')

    const loadingRequestedRef = useRef(false)
    const prevItemsLengthRef = useRef(0)
    const sentinelRef = useRef(null)

    useEffect(() => {
        if (!infinite || !hasMore || isLoading) return;
        const node = sentinelRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                dispatch(fetchNextCategoryPage(categoryId));
            }
        }, { rootMargin: '300px 0px' });

        observer.observe(node);
        return () => observer.disconnect();
    }, [infinite, hasMore, isLoading, dispatch, categoryId, items.length]);

    const [touchStart, setTouchStart] = useState(null)
    const [touchEnd, setTouchEnd] = useState(null)
    const containerRef = useRef(null)

    const minSwipeDistance = 50

    useEffect(() => {
        const updateItemsPerPage = () => {
            if (isSellers) {
                if (window.innerWidth > 1200) {
                    setItemsPerPage(8)
                } else if (window.innerWidth > 768) {
                    setItemsPerPage(6)
                } else if (window.innerWidth > 480) {
                    setItemsPerPage(4)
                } else {
                    setItemsPerPage(2)
                }
            } else {
                if (window.innerWidth > 1400) {
                    setItemsPerPage(5)
                } else if (window.innerWidth > 1200) {
                    setItemsPerPage(4)
                } else if (window.innerWidth > 768) {
                    setItemsPerPage(3)
                } else if (window.innerWidth > 480) {
                    setItemsPerPage(2)
                } else {
                    setItemsPerPage(2)
                }
            }
        }

        updateItemsPerPage()
        window.addEventListener('resize', updateItemsPerPage)
        return () => window.removeEventListener('resize', updateItemsPerPage)
    }, [isSellers])

    useEffect(() => {
        if (loadingRequestedRef.current) {
            if (!isLoading) {
                if (items.length > prevItemsLengthRef.current) {
                    setSlideDirection('next');
                    setCurrentIndex(prev => prev + 1);
                    loadingRequestedRef.current = false;
                }
            }
        }

        prevItemsLengthRef.current = items.length;
    }, [isLoading, items.length]);

    const onTouchStart = (e) => {
        setTouchEnd(null)
        setTouchStart(e.targetTouches[0].clientX)
    }

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX)
    }

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return

        const distance = touchStart - touchEnd
        const isLeftSwipe = distance > minSwipeDistance
        const isRightSwipe = distance < -minSwipeDistance

        if (isLeftSwipe) {
            handleNext()
        }
        if (isRightSwipe) {
            handlePrev()
        }
    }

    const visibleItems = items
        .slice(currentIndex * itemsPerPage, (currentIndex + 1) * itemsPerPage)

    const visibleCards = visibleItems.map((item) => (
        <ItemCard key={item.id} item={item} {...item} />
    ))

    const sellersCards = visibleItems.map((seller) => (
        <div key={seller.id} className="seller-card-showcase">
            <img
                src={seller.image || seller.images?.[0]?.src}
                alt="Seller logo"
                className="seller-logo-showcase"
            />
        </div>
    ))

    const totalCarouselPages = Math.ceil(items.length / itemsPerPage)
    const canGoPrev = currentIndex > 0
    const canGoNext = currentIndex < totalCarouselPages - 1

    const handleNext = () => {
        if (canGoNext) {
            setSlideDirection('next')
            setCurrentIndex(prev => prev + 1)
            return
        }

        if (hasMore && canLoadMore && !isLoading) {
            loadingRequestedRef.current = true;
            dispatch(fetchNextCategoryPage(categoryId))
        }
    }

    const handlePrev = () => {
        if (canGoPrev) {
            setSlideDirection('prev')
            setCurrentIndex(prev => prev - 1)
        }
    }

    if (error) {
        return (
            <div className='showcase' style={style}>
                <div className="showcase-header">
                    <div className={`showcase-title ${titleStyle}`}>{title}</div>
                </div>
                <div style={{ position: 'relative' }}>
                    <div className={`showcase-container ${isSellers ? 'showcase-sellers' : ''}`} style={{
                        visibility: 'hidden',
                        minHeight: '400px',
                        pointerEvents: 'none'
                    }}>
                        {Array(itemsPerPage).fill(null).map((_, i) => (
                            <div key={i} style={{ aspectRatio: isSellers ? '123/66' : '1/1.3' }} />
                        ))}
                    </div>
                    <div className="showcase-loader-overlay">
                        <Alert
                            message="Ошибка загрузки"
                            description={error}
                            type="error"
                            showIcon
                            style={{ maxWidth: '500px' }}
                        />
                    </div>
                </div>
            </div>
        )
    }

    if (isLoading && items.length === 0) {
        return (
            <ProductsShowcaseSkeleton
                itemsPerPage={itemsPerPage}
                isSellers={isSellers}
                title={title}
                titleStyle={titleStyle}
                description={description}
                style={style}
            />
        );
    }

    if (!isLoading && items.length === 0) {
        return (
            <div className='showcase showcase-empty' style={style}>
                <div className="showcase-header">
                    <div className={`showcase-title ${titleStyle}`}>{title}</div>
                </div>
                <div className="showcase-empty-message">
                    Нет товаров в данной категории
                </div>
            </div>
        );
    }

    if (isSellers) {
        return (
            <div className='showcase sellers-section' style={style}>
                <div className="showcase-header">
                    <div className={`showcase-title ${titleStyle}`}>{title}</div>
                    <div className="showcase-header-ctn-btn">
                        <div className="showcase-ctn-btn-arrow">
                            <button
                                className={`showcase-btn-arrow ${!canGoPrev ? 'disabled' : ''}`}
                                onClick={handlePrev}
                                disabled={!canGoPrev}
                            >
                                <LeftOutlined/>
                            </button>
                            <button
                                className={`showcase-btn-arrow ${!(canGoNext || (hasMore && canLoadMore)) ? 'disabled' : ''}`}
                                onClick={handleNext}
                                disabled={!(canGoNext || (hasMore && canLoadMore)) || isLoading}
                            >
                                <RightOutlined/>
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    style={{ position: 'relative' }}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    ref={containerRef}
                >
                    <div
                        key={currentIndex}
                        className={`showcase-container showcase-sellers slide-${slideDirection}`}
                    >
                        {sellersCards}
                    </div>

                    {isLoading && items.length > 0 && (
                        <div className="showcase-loader-overlay" />
                    )}
                </div>

                <div style={{
                    textAlign: 'center',
                    color: '#999',
                    marginTop: '20px',
                    fontSize: '14px',
                }}>
                    Страница {currentIndex + 1} из {totalCarouselPages}
                    {' • '}
                    Показано {items.length} из {paginationInfo.total || items.length}
                    {paginationInfo.totalPages > 0 && (
                        <span> • Загружено {paginationInfo.currentPage} из {paginationInfo.totalPages} страниц с сервера</span>
                    )}
                    {hasMore && ' • Есть еще'}
                </div>
            </div>
        )
    }

    if (infinite) {
        return (
            <div className='showcase showcase-infinite' style={style}>
                <div className="showcase-header">
                    <div className={`showcase-title ${titleStyle}`}>{title}</div>
                    <div className='showcase-description'>
                        {description ?? ''}
                    </div>
                    <div className="showcase-header-ctn-btn">
                        <button className="showcase-header-btn" onClick={() => navigate(`/products/${categoryId}`)}>
                            Смотреть товары
                        </button>
                    </div>
                </div>

                <div className="showcase-container showcase-container-grid">
                    {items.map((item) => (
                        <ItemCard key={item.id} item={item} {...item} />
                    ))}
                </div>

                {isLoading && items.length > 0 && (
                    <div className="showcase-container showcase-container-grid showcase-loadmore">
                        {Array(itemsPerPage).fill(null).map((_, i) => (
                            <ItemCardSkeleton key={i} />
                        ))}
                    </div>
                )}

                {hasMore && <div ref={sentinelRef} className="showcase-sentinel" aria-hidden="true" />}
            </div>
        )
    }

    return (
        <div className='showcase' style={style}>
            <div className="showcase-header">
                <div className={`showcase-title ${titleStyle}`}>{title}</div>
                <div className='showcase-description'>
                    {description ?? ''}
                </div>
                <div className="showcase-header-ctn-btn">
                    <button className="showcase-header-btn" onClick={() => navigate(`/products/${categoryId}`)}>
                        Смотреть товары
                    </button>

                    <div className="showcase-ctn-btn-arrow">
                        <button
                            className={`showcase-btn-arrow ${!canGoPrev ? 'disabled' : ''}`}
                            onClick={handlePrev}
                            disabled={!canGoPrev}
                        >
                            <LeftOutlined/>
                        </button>
                        <button
                            className={`showcase-btn-arrow ${!(canGoNext || (hasMore && canLoadMore)) ? 'disabled' : ''}`}
                            onClick={handleNext}
                            disabled={!(canGoNext || (hasMore && canLoadMore)) || isLoading}
                        >
                            <RightOutlined/>
                        </button>
                    </div>
                </div>
            </div>

            <div
                style={{ position: 'relative' }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                ref={containerRef}
            >
                <div
                    key={currentIndex}
                    className={`showcase-container slide-${slideDirection}`}
                >
                    {visibleCards}
                </div>

                {isLoading && items.length > 0 && (
                    <div className="showcase-loader-overlay" />
                )}
            </div>

            {/*<div style={{*/}
            {/*    textAlign: 'center',*/}
            {/*    color: '#999',*/}
            {/*    marginTop: '20px',*/}
            {/*    fontSize: '14px',*/}
            {/*}}>*/}

            {/*    {' • '}*/}

            {/*</div>*/}
        </div>
    )
}

export default ProductsShowcase